'use client';

import * as React from 'react';
import type { Table } from '@tanstack/react-table';
import { Search, Filter, Download, Eye, X, RotateCcw } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { cn } from '@/lib/utils';

/**
 * Filter information for displaying active filter chips
 */
export interface ActiveFilter {
  /** Unique identifier for the filter */
  id: string;
  /** Display label for the filter */
  label: string;
  /** Callback when filter is removed */
  onRemove: () => void;
}

/**
 * Props for the DataTableToolbar component
 */
export interface DataTableToolbarProps<TData> {
  /** TanStack Table instance */
  table: Table<TData>;
  /** Callback for export functionality */
  onExport?: ((format: 'csv' | 'excel' | 'pdf') => void) | undefined;
  /** Placeholder text for the search input */
  searchPlaceholder?: string | undefined;
  /** Callback to toggle the filter builder dialog */
  onToggleFilterBuilder?: (() => void) | undefined;
  /** Array of active filters to display as chips */
  activeFilters?: ActiveFilter[] | undefined;
  /** Callback when clear all filters is clicked */
  onClearAllFilters?: (() => void) | undefined;
  /** Whether the filter builder is currently open */
  filterBuilderOpen?: boolean | undefined;
  /** Enable column reordering controls */
  enableColumnReordering?: boolean | undefined;
  /** Enable filtering (controls search input visibility) */
  enableFiltering?: boolean | undefined;
  /** Enable the column visibility toggle */
  enableColumnVisibility?: boolean | undefined;
}

/**
 * DataTableToolbar Component
 *
 * A comprehensive toolbar for data tables that includes global search, column visibility controls,
 * export options, filter builder toggle, and active filter management.
 *
 * Features:
 * - Debounced global search (300ms)
 * - Column visibility toggles with checkboxes
 * - Export dropdown (CSV, Excel, PDF)
 * - Advanced filter builder toggle
 * - Active filter chips with remove capability
 * - Clear all filters functionality
 *
 * @example
 * ```tsx
 * <DataTableToolbar
 *   table={table}
 *   searchPlaceholder="Search records..."
 *   onExport={(format) => handleExport(format)}
 *   onToggleFilterBuilder={() => setFilterBuilderOpen(true)}
 *   activeFilters={[
 *     { id: '1', label: 'Status: Active', onRemove: () => removeFilter('1') }
 *   ]}
 *   onClearAllFilters={() => clearAllFilters()}
 * />
 * ```
 */
export function DataTableToolbar<TData>({
  table,
  onExport,
  searchPlaceholder = 'Search...',
  onToggleFilterBuilder,
  activeFilters = [],
  onClearAllFilters,
  filterBuilderOpen = false,
  enableColumnReordering = false,
  enableFiltering = true,
  enableColumnVisibility = true,
}: DataTableToolbarProps<TData>) {
  const [searchValue, setSearchValue] = React.useState('');
  const [debouncedSearchValue, setDebouncedSearchValue] = React.useState('');

  // Debounce search input (300ms)
  React.useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchValue(searchValue);
    }, 300);

    return () => clearTimeout(timer);
  }, [searchValue]);

  // Apply debounced search to table global filter
  React.useEffect(() => {
    table.setGlobalFilter(debouncedSearchValue);
  }, [debouncedSearchValue, table]);

  // Get all columns that can be hidden
  const columns = table.getAllColumns().filter((column) => column.getCanHide());

  // Check if there are any active filters
  const hasActiveFilters = activeFilters.length > 0;

  return (
    <div className="space-y-3">
      {/* Top row: Controls */}
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        {/* Left side: Search (only show if filtering is enabled) */}
        {enableFiltering && (
          <div className="flex-1 sm:max-w-md">
            <Input
              type="search"
              placeholder={searchPlaceholder}
              value={searchValue}
              onChange={(e) => setSearchValue(e.target.value)}
              leftIcon={<Search className="h-4 w-4" />}
              rightIcon={
                searchValue ? (
                  <button
                    onClick={() => setSearchValue('')}
                    className="pointer-events-auto cursor-pointer"
                    aria-label="Clear search"
                  >
                    <X className="h-4 w-4" />
                  </button>
                ) : undefined
              }
              className="w-full"
            />
          </div>
        )}

        {/* Right side: Actions */}
        <div className={cn("flex flex-wrap items-center gap-2", !enableFiltering && "ml-auto")}>
          {/* Column Visibility Toggle */}
          {enableColumnVisibility && columns.length > 0 && (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" size="md" leftIcon={<Eye className="h-4 w-4" />}>
                  Columns
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56">
                <DropdownMenuLabel>Toggle columns</DropdownMenuLabel>
                <DropdownMenuSeparator />
                {columns.map((column) => {
                  const columnName =
                    typeof column.columnDef.header === 'string'
                      ? column.columnDef.header
                      : column.id;

                  return (
                    <DropdownMenuCheckboxItem
                      key={column.id}
                      checked={column.getIsVisible()}
                      onCheckedChange={(value) => column.toggleVisibility(!!value)}
                    >
                      {columnName}
                    </DropdownMenuCheckboxItem>
                  );
                })}
              </DropdownMenuContent>
            </DropdownMenu>
          )}

          {/* Export Dropdown */}
          {onExport && (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="outline"
                  size="md"
                  leftIcon={<Download className="h-4 w-4" />}
                >
                  Export
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuLabel>Export as</DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={() => onExport('csv')}>
                  Export as CSV
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => onExport('excel')}>
                  Export as Excel
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => onExport('pdf')}>
                  Export as PDF
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          )}

          {/* Advanced Filters Button */}
          {onToggleFilterBuilder && (
            <Button
              variant={filterBuilderOpen ? 'primary' : 'outline'}
              size="md"
              leftIcon={<Filter className="h-4 w-4" />}
              onClick={onToggleFilterBuilder}
            >
              Filters
            </Button>
          )}

          {/* Reset Column Order Button */}
          {enableColumnReordering && (
            <Button
              variant="ghost"
              size="md"
              leftIcon={<RotateCcw className="h-4 w-4" />}
              onClick={() => table.resetColumnOrder()}
              className="text-text-secondary"
              aria-label="Reset column order to default"
            >
              Reset Order
            </Button>
          )}

          {/* Clear All Filters Button */}
          {hasActiveFilters && onClearAllFilters && (
            <Button
              variant="ghost"
              size="md"
              onClick={onClearAllFilters}
              className="text-text-secondary"
            >
              Clear all
            </Button>
          )}
        </div>
      </div>

      {/* Bottom row: Active Filter Chips */}
      {hasActiveFilters && (
        <div className="flex flex-wrap items-center gap-2">
          {activeFilters.map((filter) => (
            <Badge
              key={filter.id}
              variant="default"
              size="md"
              onRemove={filter.onRemove}
            >
              {filter.label}
            </Badge>
          ))}
        </div>
      )}
    </div>
  );
}
